import React from "react";
import ServiceLayout from "../../components/services/ServiceLayout";
import { Zap, AlertTriangle, Activity, ShieldCheck, Thermometer, Droplets, Info } from "lucide-react";
import image1 from '../../Assets/Services/periodontal1.jpg'
import image2 from '../../Assets/Services/periodontal2.jpg'


const PeriodontalTreatment = () => {
  return (
    <ServiceLayout
      title="Periodontal Treatment"
      subtitle="Treat gum disease, bleeding gums, and infections with advanced periodontal care and modern
laser therapy for healthier gums."
      heroImage="/images/hero/gum-care.jpg"
      images={[image1, image2]}
    >
      {/* Introduction */}
      <section className="mb-12">
        <div className="flex items-start gap-4 bg-blue-50 p-6 rounded-2xl border-l-8 border-blue-600">
          <Info className="text-blue-600 w-8 h-8 flex-shrink-0 mt-1" />
          <p className="text-lg text-gray-700 leading-relaxed">
            Healthy gums are the foundation of a beautiful and healthy smile. Gum diseases, also known as
periodontal diseases, are among the most common dental problems affecting adults. If left
untreated, they can lead to gum damage, bone loss, and even tooth loss.
          </p>
        </div>
        <p className="mt-6 text-gray-700">
          At Dr. Sridhar International Dental Hospitals, we offer advanced periodontal treatments in
Vijayawada and Hyderabad using modern technology, including the advanced Fotona multipurpose
dental laser, to effectively treat gum problems and restore your oral health.
        </p>
      </section>

      {/* Disease Progression / Stages */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Stages of Periodontal Disease</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="relative p-6 bg-white border border-yellow-200 rounded-xl shadow-sm">
            <div className="absolute top-0 right-0 p-2 bg-yellow-100 text-yellow-700 text-xs font-bold rounded-bl-lg">STAGE 1</div>
            <h4 className="font-bold text-yellow-700 mb-2">Gingivitis</h4>
            <p className="text-sm text-gray-600">Early stage of gum disease where gums become inflamed and may bleed while brushing or flossing.</p>
          </div>
          <div className="relative p-6 bg-white border border-orange-200 rounded-xl shadow-sm">
            <div className="absolute top-0 right-0 p-2 bg-orange-100 text-orange-700 text-xs font-bold rounded-bl-lg">STAGE 2</div>
            <h4 className="font-bold text-orange-700 mb-2">Periodontitis</h4>
            <p className="text-sm text-gray-600">Moderate stage of gum disease where infection spreads deeper into the gums and begins affecting
the supporting bone.</p>
          </div>
          <div className="relative p-6 bg-white border border-red-200 rounded-xl shadow-sm">
            <div className="absolute top-0 right-0 p-2 bg-red-100 text-red-700 text-xs font-bold rounded-bl-lg">STAGE 3</div>
            <h4 className="font-bold text-red-700 mb-2">Severe Periodontitis</h4>
            <p className="text-sm text-gray-600">Advanced stage of gum disease causing significant damage to gums and bone, which can eventually
lead to tooth loss if untreated.</p>
          </div>
        </div>
        <p className="mt-6 text-red-600 font-medium flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" /> Early diagnosis and treatment are essential to prevent long-term damage.
        </p>
      </section>

      {/* Symptoms Grid */}
      <section className="mb-12 bg-gray-50 p-8 rounded-3xl">
        <p className="font-medium  text-gray-900 mb-8">If you notice symptoms such as bleeding gums, persistent bad breath, gum swelling, or loose teeth,
it may be a sign of periodontal disease. Early treatment can help stop the progression of the
disease and protect your oral health.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: <Droplets className="text-red-500" />, text: "Bleeding gums during brushing" },
            { icon: <Activity className="text-red-500" />, text: "Swollen or red gums" },
            { icon: <Thermometer className="text-red-500" />, text: "Persistent bad breath" },
            { icon: <ShieldCheck className="text-red-500" />, text: "Gum recession (receding gums)" },
            { icon: <Activity className="text-red-500" />, text: "Loose or shifting teeth" },
            { icon: <AlertTriangle className="text-red-500" />, text: "Pain while chewing" },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              {item.icon}
              <span className="font-medium text-gray-700 text-sm">{item.text}</span>
            </div>
          ))}
        </div>
        <p className="font-medium  text-gray-900 mb-8">At Dr. Sridhar International Dental Hospitals, our specialists use advanced diagnostic techniques
and modern laser technology to effectively treat gum disease and restore healthy gums.</p>
      </section>

      {/* Laser Technology Highlight */}
      <section className="mb-12">
        <div className="bg-blue-900 text-white p-8 md:p-12 rounded-3xl shadow-2xl relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3 !text-blue-300">
              <Zap className="text-blue-300" /> Laser Treatment with Fotona </h2>
            <p className="!text-blue-100 text-lg mb-8 leading-relaxed max-w-2xl">
              We use the advanced Fotona multipurpose dental laser for both hard and soft tissue procedures. 
              Laser dentistry has significantly improved the effectiveness and comfort of gum disease treatment.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                "Minimally invasive procedures",
                "Reduced bleeding during treatment",
                "Faster healing of gum tissues",
                "Minimal discomfort and swelling",
                "Superior treatment outcomes",
              ].map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-3 text-blue-50">
                  <ShieldCheck className="text-blue-400 w-5 h-5 flex-shrink-0" />
                  <span className="font-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
          <Zap className="absolute -bottom-10 -right-10 w-72 h-72 text-white/5 rotate-12" />
        </div>
      </section>

      {/* Importance of Treatment */}
      <section className="border-t pt-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Importance of Early Treatment</h2>
        <p className="text-gray-700 leading-relaxed mb-6">
          Untreated periodontal (gum) disease is one of the leading causes of tooth loss in adults. Detecting
and treating gum infections early can help protect your natural teeth and prevent serious
complications such as bone damage and chronic oral infections.
        </p>
        <div className="p-6 bg-red-50 border border-red-100 rounded-xl">
          <p className="font-bold text-red-700">At Dr. Sridhar International Dental Hospitals, our specialists use advanced laser technology and
modern treatment protocols to effectively Importance of Early Treatment <br/>Protect the foundation of your smile. Schedule your gum health check-up today.</p>
        </div>
      </section>
    </ServiceLayout>
  );
};

export default PeriodontalTreatment;